import React, { FC, useState } from 'react'
import { Table, Button, Empty, Space } from 'antd'
import {
  EditOutlined,
  PlusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { ICustomerWithPricing } from '@/supabase/Customer/customer.interface'
import {
  IRegularCustomerTableProps,
  ICustomerPricingFormValues,
} from '../interfaces'
import { InlineEditableRow } from './InlineEditableRow'
import { InlineCreateRow } from './InlineCreateRow'
import { formatCurrency } from '../utils/formatters'

export const RegularCustomerTable: FC<IRegularCustomerTableProps> = ({
  customers,
  onEditCustomer,
  onCreateCustomer,
  onUpdateRanking,
  onDeleteCustomer,
  loading = false,
}) => {
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(
    null
  )
  const [isCreating, setIsCreating] = useState(false)
  const formatPrice = (price: number | undefined) => {
    return formatCurrency(price)
  }

  const handleEditClick = (customer: ICustomerWithPricing) => {
    setEditingCustomerId(customer.id)
  }

  const handleCreateClick = () => {
    setIsCreating(true)
  }

  const handleSaveInlineEdit = async (
    customer: ICustomerWithPricing,
    formData: ICustomerPricingFormValues
  ) => {
    await onEditCustomer(customer, formData)
    setEditingCustomerId(null)
  }

  const handleSaveInlineCreate = async (
    formData: ICustomerPricingFormValues
  ) => {
    await onCreateCustomer(formData)
    setIsCreating(false)
  }

  const handleCancelInlineEdit = () => {
    setEditingCustomerId(null)
  }

  const handleCancelInlineCreate = () => {
    setIsCreating(false)
  }

  const handleDeleteCustomer = async (customerId: string) => {
    await onDeleteCustomer(customerId)
    setEditingCustomerId(null)
  }

  const handleRankingUp = (customer: ICustomerWithPricing) => {
    onUpdateRanking(customer.id, 'up')
  }

  const handleRankingDown = (customer: ICustomerWithPricing) => {
    onUpdateRanking(customer.id, 'down')
  }

  const columns: ColumnsType<ICustomerWithPricing> = [
    {
      title: '#',
      dataIndex: 'ranking',
      key: 'ranking',
      width: '10%',
      align: 'center',
      sorter: (a, b) => (a.ranking || 0) - (b.ranking || 0),
      render: (ranking: number, record: ICustomerWithPricing) => (
        <div className='flex items-center justify-center space-x-1'>
          <span className='font-medium text-blue-600'>{ranking || '-'}</span>
          <div className='flex flex-col'>
            <Button
              type='text'
              size='small'
              icon={<ArrowUpOutlined />}
              onClick={() => handleRankingUp(record)}
              disabled={
                ranking <= 1 ||
                loading ||
                editingCustomerId !== null ||
                isCreating
              }
              className='h-4 w-4 p-0 flex items-center justify-center'
            />
            <Button
              type='text'
              size='small'
              icon={<ArrowDownOutlined />}
              onClick={() => handleRankingDown(record)}
              disabled={
                ranking >= customers.length ||
                loading ||
                editingCustomerId !== null ||
                isCreating
              }
              className='h-4 w-4 p-0 flex items-center justify-center'
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string) => (
        <span className='font-medium text-gray-800'>{name}</span>
      ),
    },
    {
      title: 'ใหญ่',
      key: 'large_price',
      width: '15%',
      align: 'right',
      render: (_, record) => (
        <span className='text-gray-700 font-mono'>
          {formatPrice(record.pricing?.large_ice_cube_price_thb)}
        </span>
      ),
    },
    {
      title: 'เล็ก',
      key: 'small_price',
      width: '15%',
      align: 'right',
      render: (_, record) => (
        <span className='text-gray-700 font-mono'>
          {formatPrice(record.pricing?.small_ice_cube_price_thb)}
        </span>
      ),
    },
    {
      title: 'โม่',
      key: 'crushed_price',
      width: '15%',
      align: 'right',
      render: (_, record) => (
        <span className='text-gray-700 font-mono'>
          {formatPrice(record.pricing?.crushed_ice_price_thb)}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '25%',
      align: 'right',
      render: (_, record) => (
        <div className='flex justify-end'>
          <Button
            type='default'
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}
            className='flex items-center justify-center'
            disabled={editingCustomerId !== null || isCreating}
          >
            Edit
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className='bg-white border rounded-lg p-6 shadow-sm'>
      <div className='flex justify-between items-center mb-6'>
        <h2 className='text-lg font-semibold text-gray-800'>Customer List</h2>
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={handleCreateClick}
          className='flex items-center'
          disabled={editingCustomerId !== null || isCreating}
        >
          Add Customer
        </Button>
      </div>

      {/* Inline Create Form */}
      {isCreating && (
        <div className='mb-4'>
          <InlineCreateRow
            onSave={handleSaveInlineCreate}
            onCancel={handleCancelInlineCreate}
            loading={loading}
          />
        </div>
      )}

      <Table
        columns={columns}
        dataSource={customers}
        rowKey='id'
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        locale={{
          emptyText: (
            <Empty
              description='No customer data found'
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        scroll={{ x: 800 }}
        className='custom-table'
        expandable={{
          expandedRowRender: record => {
            if (editingCustomerId === record.id) {
              return (
                <InlineEditableRow
                  customer={record}
                  onSave={handleSaveInlineEdit}
                  onCancel={handleCancelInlineEdit}
                  onDelete={handleDeleteCustomer}
                  loading={loading}
                />
              )
            }
            return null
          },
          expandedRowKeys: editingCustomerId ? [editingCustomerId] : [],
          showExpandColumn: false,
          expandRowByClick: false,
        }}
      />
    </div>
  )
}
